import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { AssetClass } from '@monorepo/types';
import { useDashboard } from '../../../../../contexts';
import { useAccountStatisticsData } from '../../../../../hooks';
import { formatGrowthPercentage, formatNumber } from '../../utils';
import { ForexStatisticsGrid } from '../forex-statistics-grid';
import { StatisticCard } from '../statistic-card';

export interface StatisticCardProps {
  label: string;
  tooltip: string;
  value: string | number;
}

export const StatisticsGrid: FC = () => {
  const { account, statistics: contextStats, assetType } = useDashboard();

  const { t } = useTranslation('accountStatistics');

  const { data: accountStats } = useAccountStatisticsData(
    account?.tsAccountId || '',
    account || undefined,
  );

  if (!account) {
    return null;
  }

  if (assetType === AssetClass.Forex) {
    return <ForexStatisticsGrid />;
  }

  const { highWaterMark = 0, growthPercentage = 0 } = contextStats || {};

  if (!accountStats) {
    return null;
  }

  const statisticsData: StatisticCardProps[] = [
    {
      label: t('statistics.profitFactor.label'),
      tooltip: t('statistics.profitFactor.tooltip'),
      value: accountStats.profitFactor?.toFixed(2) || 0,
    },
    {
      label: t('statistics.expectancy.label'),
      tooltip: t('statistics.expectancy.tooltip'),
      value: accountStats.expectancy
        ? `$${formatNumber(accountStats.expectancy)}`
        : '$0.00',
    },
    {
      label: t('statistics.avgProfit.label'),
      tooltip: t('statistics.avgProfit.tooltip'),
      value: accountStats.avgProfit
        ? `$${formatNumber(accountStats.avgProfit)}`
        : '$0.00',
    },
    {
      label: t('statistics.avgLoss.label'),
      tooltip: t('statistics.avgLoss.tooltip'),
      value: accountStats.avgLoss
        ? `-$${formatNumber(Math.abs(accountStats.avgLoss))}`
        : '$0.00',
    },
    {
      label: t('statistics.noOfTrades.label'),
      tooltip: t('statistics.noOfTrades.tooltip'),
      value: accountStats.totalTrades
        ? accountStats.totalTrades.toLocaleString('en-US')
        : 0,
    },
    {
      label: t('statistics.bestTrade.label'),
      tooltip: t('statistics.bestTrade.tooltip'),
      value: accountStats.bestProfit
        ? `$${formatNumber(accountStats.bestProfit)}`
        : '$0.00',
    },
    {
      label: t('statistics.worstTrade.label'),
      tooltip: t('statistics.worstTrade.tooltip'),
      value: accountStats.biggestLoss
        ? `-$${formatNumber(Math.abs(accountStats.biggestLoss))}`
        : '$0.00',
    },
    {
      label: t('statistics.hwm.label'),
      tooltip: t('statistics.hwm.tooltip'),
      value: highWaterMark ? `$${formatNumber(highWaterMark)}` : '$0.00',
    },
    {
      label: t('statistics.growth.label'),
      tooltip: t('statistics.growth.tooltip'),
      value: formatGrowthPercentage(growthPercentage),
    },
  ];

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Grid container spacing={6}>
        {statisticsData.map((stat: StatisticCardProps, index: number) => (
          <Grid size={{ xs: 6, md: 12 / 3 }} key={index}>
            <StatisticCard
              label={stat.label}
              tooltip={stat.tooltip}
              value={stat.value}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
